import { useCallback, useEffect, useState } from 'react';

const CACHE_CONFIG = {
  CACHE_NAME: 'video-cache-v1',
  MAX_CACHE_ENTRIES: 10,
  CACHE_DELAY: 2000,
  MAX_CACHE_SIZE: 50 * 1024 * 1024 // 50MB total cache limit
};

/**
 * Normalize video URL to ensure it has the proper protocol
 * @param url - The video URL to normalize
 * @returns The normalized URL with https:// prefix if missing
 */
const normalizeVideoUrl = (url: string): string => {
  if (!url) return url;

  // If URL already has a protocol, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // If URL starts with //, it's a protocol-relative URL, add https:
  if (url.startsWith('//')) {
    return `https:${url}`;
  }

  // If URL doesn't have a protocol, assume it's from Contentful CDN and add https://
  return `https://${url}`;
};

const cleanupExpiredCache = async (): Promise<void> => {
  try {
    const cache = await caches.open(CACHE_CONFIG.CACHE_NAME);
    const today = new Date().toDateString();
    const lastCleanup = localStorage.getItem('video-cache-last-cleanup');

    if (lastCleanup !== today) {
      const keys = await cache.keys();
      for (const key of keys) {
        const response = await cache.match(key);
        const cachedDate = response?.headers.get('x-cached-date');

        // Delete entries older than today
        if (!cachedDate || cachedDate !== today) {
          await cache.delete(key);
        }
      }
      localStorage.setItem('video-cache-last-cleanup', today);
    }
  } catch (error) {
    console.warn('Cache cleanup failed:', error);
  }
};

const limitCacheSize = async (): Promise<void> => {
  try {
    const cache = await caches.open(CACHE_CONFIG.CACHE_NAME);
    const keys = await cache.keys();

    if (keys.length > CACHE_CONFIG.MAX_CACHE_ENTRIES) {
      const entries = await Promise.all(
        keys.map(async (key) => {
          const response = await cache.match(key);
          const cachedTime = response?.headers.get('x-cached-time') ?? '0';
          const size = parseInt(
            response?.headers.get('content-length') ?? '0',
            10
          );
          return { key, time: parseInt(cachedTime, 10), size };
        })
      );

      // Sort by time (oldest first) and size (largest first for same time)
      entries.sort((a, b) => a.time - b.time || b.size - a.size);
      const toDelete = entries.slice(
        0,
        keys.length - CACHE_CONFIG.MAX_CACHE_ENTRIES
      );

      for (const entry of toDelete) {
        await cache.delete(entry.key);
      }
    }

    // Also check total cache size
    const totalSize = (
      await Promise.all(
        keys.map(async (key) => {
          const response = await cache.match(key);
          return parseInt(response?.headers.get('content-length') ?? '0', 10);
        })
      )
    ).reduce((sum, size) => sum + size, 0);

    if (totalSize > CACHE_CONFIG.MAX_CACHE_SIZE) {
      // Delete oldest entries until under size limit
      const entries = await Promise.all(
        keys.map(async (key) => {
          const response = await cache.match(key);
          const cachedTime = response?.headers.get('x-cached-time') ?? '0';
          const size = parseInt(
            response?.headers.get('content-length') ?? '0',
            10
          );
          return { key, time: parseInt(cachedTime, 10), size };
        })
      );

      entries.sort((a, b) => a.time - b.time);
      let currentSize = totalSize;

      for (const entry of entries) {
        if (currentSize <= CACHE_CONFIG.MAX_CACHE_SIZE) break;
        await cache.delete(entry.key);
        currentSize -= entry.size;
      }
    }
  } catch (error) {
    console.warn('Cache size limit failed:', error);
  }
};

/**
 * Video caching utility that stores video files in browser Cache API
 * and returns blob URLs for immediate use
 * Optimized for performance - returns original URL immediately and caches in background
 */
export const cacheVideo = async (
  url: string,
  options: {
    immediate?: boolean;
    priority?: 'high' | 'low';
  } = {}
): Promise<string> => {
  if (!url || typeof window === 'undefined') return url;

  // Normalize the URL to ensure it has the proper protocol
  const normalizedUrl = normalizeVideoUrl(url);
  const { immediate = false, priority = 'low' } = options;

  try {
    const cache = await caches.open(CACHE_CONFIG.CACHE_NAME);
    const cachedResponse = await cache.match(normalizedUrl);

    if (cachedResponse) {
      const cachedDate = cachedResponse.headers.get('x-cached-date');
      const today = new Date().toDateString();

      // Check if cache is still valid (same day)
      if (cachedDate === today) {
        const blob = await cachedResponse.blob();
        return URL.createObjectURL(blob);
      } else {
        // Cache expired, delete it
        await cache.delete(normalizedUrl);
      }
    }

    // If not cached and not immediate, return original URL and cache in background
    if (!immediate && priority === 'low') {
      // Background caching with delay to avoid blocking initial page load
      setTimeout(async () => {
        try {
          const response = await fetch(normalizedUrl);
          if (response.ok) {
            const responseClone = response.clone();
            const headers = new Headers(responseClone.headers);
            headers.set('x-cached-time', Date.now().toString());
            headers.set('x-cached-date', new Date().toDateString());

            const modifiedResponse = new Response(responseClone.body, {
              status: responseClone.status,
              statusText: responseClone.statusText,
              headers
            });

            await cache.put(normalizedUrl, modifiedResponse);
            await cleanupExpiredCache();
            await limitCacheSize();
          }
        } catch (error) {
          console.warn('Background video caching failed:', error);
        }
      }, CACHE_CONFIG.CACHE_DELAY);

      return normalizedUrl;
    }

    // Immediate caching (for high priority or when explicitly requested)
    const response = await fetch(normalizedUrl);
    if (response.ok) {
      const responseClone = response.clone();
      const headers = new Headers(responseClone.headers);
      headers.set('x-cached-time', Date.now().toString());
      headers.set('x-cached-date', new Date().toDateString());

      const modifiedResponse = new Response(responseClone.body, {
        status: responseClone.status,
        statusText: responseClone.statusText,
        headers
      });

      await cache.put(normalizedUrl, modifiedResponse);
      await cleanupExpiredCache();
      await limitCacheSize();

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    }
  } catch (error) {
    console.warn('Video caching failed:', error);
  }

  return normalizedUrl; // Fallback to normalized URL
};

/**
 * React hook for caching video assets with performance optimization
 */
export const useVideoCache = (
  videoUrl: string | undefined,
  options: { priority?: 'high' | 'low'; immediate?: boolean } = {}
): string => {
  const normalizedVideoUrl = videoUrl ? normalizeVideoUrl(videoUrl) : '';
  const [cachedUrl, setCachedUrl] = useState<string>(normalizedVideoUrl);
  const { priority = 'low', immediate = false } = options;

  useEffect(() => {
    if (!videoUrl) {
      setCachedUrl('');
      return;
    }

    if (!cachedUrl || cachedUrl === '') {
      setCachedUrl(normalizedVideoUrl);
    }

    const cacheVideoAsync = async () => {
      try {
        const cached = await cacheVideo(normalizedVideoUrl, {
          immediate,
          priority
        });
        setCachedUrl(cached);
      } catch (error) {
        console.warn('Video caching failed:', error);
        setCachedUrl(normalizedVideoUrl);
      }
    };

    cacheVideoAsync();

    return () => {
      if (cachedUrl?.startsWith('blob:') && cachedUrl !== normalizedVideoUrl) {
        URL.revokeObjectURL(cachedUrl);
      }
    };
  }, [normalizedVideoUrl, priority, immediate]);

  return cachedUrl ?? normalizedVideoUrl ?? '';
};

/**
 * React hook for caching multiple video assets with performance optimization
 * @param videoUrls - Array of video URLs to cache
 * @param options - Caching options
 * @returns Array of cached video URLs
 */
export const useMultipleVideoCache = (
  videoUrls: (string | undefined)[],
  options: { priority?: 'high' | 'low'; immediate?: boolean } = {}
): string[] => {
  const normalizedVideoUrls = videoUrls.map((url) =>
    url ? normalizeVideoUrl(url) : ''
  );
  const [cachedUrls, setCachedUrls] = useState<string[]>(
    () => normalizedVideoUrls
  );
  const { priority = 'low', immediate = false } = options;

  // Use JSON.stringify to create a stable dependency
  const videoUrlsString = JSON.stringify(normalizedVideoUrls);
  const optionsString = JSON.stringify({ priority, immediate });

  useEffect(() => {
    const urls = JSON.parse(videoUrlsString) as string[];

    // Set normalized URLs immediately to prevent infinite loops
    setCachedUrls(urls);

    const cacheVideos = async () => {
      if (urls.some((url) => url)) {
        try {
          const promises = urls.map(async (url) => {
            if (!url) return '';
            const cached = await cacheVideo(url, { immediate, priority });
            return cached;
          });

          const results = await Promise.all(promises);
          setCachedUrls(results);
        } catch (error) {
          console.warn('Multiple video caching failed:', error);
          // Fallback to normalized URLs
          setCachedUrls(urls);
        }
      }
    };

    cacheVideos();

    // Cleanup blob URLs on unmount
    return () => {
      cachedUrls.forEach((url) => {
        if (url && url.startsWith('blob:') && !urls.includes(url)) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [videoUrlsString, optionsString]);

  return cachedUrls;
};

/**
 * Clear all cached videos
 */
export const clearVideoCache = async (): Promise<void> => {
  try {
    await caches.delete(CACHE_CONFIG.CACHE_NAME);
  } catch (error) {
    console.warn('Failed to clear video cache:', error);
  }
};

/**
 * Prefetch video on hover for better UX
 * @param videoUrl - The URL of the video to prefetch
 * @returns Hover handlers for prefetching
 */
export const usePrefetchOnHover = (videoUrl: string | undefined) => {
  const [isPrefetching, setIsPrefetching] = useState(false);
  const normalizedVideoUrl = videoUrl ? normalizeVideoUrl(videoUrl) : '';

  const handleMouseEnter = useCallback(() => {
    if (!normalizedVideoUrl || isPrefetching) return;

    setIsPrefetching(true);
    cacheVideo(normalizedVideoUrl, { immediate: true, priority: 'high' })
      .catch((error) => console.warn('Prefetch failed:', error))
      .finally(() => setIsPrefetching(false));
  }, [normalizedVideoUrl, isPrefetching]);

  return {
    onMouseEnter: handleMouseEnter,
    isPrefetching
  };
};

/**
 * Initialize day-based video caching
 * This runs when the module is first loaded
 */
const initializeDayCache = () => {
  if (typeof window !== 'undefined') {
    cleanupExpiredCache().catch((error) =>
      console.warn('Failed to initialize day cache:', error)
    );
  }
};

initializeDayCache();
